import { useCallback, useEffect, useReducer, useRef } from 'react'
import {
  WorkerJobError,
  isAbortError,
  type WorkerErrorPayload,
  type WorkerProgress,
} from './protocol'
import { runWorkerJob, type RunWorkerJobOptions } from './runner'

export type WorkerJobStatus =
  | 'idle'
  | 'validating'
  | 'running'
  | 'success'
  | 'cancelled'
  | 'recoverable-error'
  | 'fatal-error'

export interface WorkerJobState<Result> {
  status: WorkerJobStatus
  progress: WorkerProgress | null
  result: Result | null
  error: WorkerErrorPayload | null
}

export type WorkerJobAction<Result> =
  | { type: 'validate' }
  | { type: 'progress'; progress: WorkerProgress }
  | { type: 'success'; result: Result }
  | { type: 'cancel' }
  | { type: 'error'; error: WorkerErrorPayload }
  | { type: 'reset' }

export function createInitialWorkerJobState<Result>(): WorkerJobState<Result> {
  return { status: 'idle', progress: null, result: null, error: null }
}

export function workerJobReducer<Result>(
  state: WorkerJobState<Result>,
  action: WorkerJobAction<Result>,
): WorkerJobState<Result> {
  switch (action.type) {
    case 'validate':
      return { status: 'validating', progress: null, result: null, error: null }
    case 'progress':
      return { ...state, status: 'running', progress: action.progress }
    case 'success':
      return { status: 'success', progress: null, result: action.result, error: null }
    case 'cancel':
      return { status: 'cancelled', progress: null, result: null, error: null }
    case 'error':
      return {
        status: action.error.kind === 'recoverable' ? 'recoverable-error' : 'fatal-error',
        progress: null,
        result: null,
        error: action.error,
      }
    case 'reset':
      return createInitialWorkerJobState()
  }
}

interface HookOptions<Input, Result> extends Omit<
  RunWorkerJobOptions<Input, Result>,
  'input' | 'signal' | 'onProgress'
> {
  onSuccess?: (result: Result) => void
  onError?: (error: WorkerErrorPayload) => void
  onCancel?: () => void
}

export function useWorkerJob<Input, Result>(options: HookOptions<Input, Result>) {
  const [state, dispatch] = useReducer(
    workerJobReducer<Result>,
    undefined,
    createInitialWorkerJobState<Result>,
  )
  const optionsRef = useRef(options)
  const controllerRef = useRef<AbortController | null>(null)
  const generationRef = useRef(0)
  optionsRef.current = options

  const cancel = useCallback(() => {
    const controller = controllerRef.current
    if (!controller) return

    generationRef.current += 1
    controller.abort()
    controllerRef.current = null
    dispatch({ type: 'cancel' })
    invokeSafely(() => optionsRef.current.onCancel?.())
  }, [])

  const reset = useCallback(() => {
    generationRef.current += 1
    controllerRef.current?.abort()
    controllerRef.current = null
    dispatch({ type: 'reset' })
  }, [])

  const run = useCallback(async (input: Input): Promise<Result | undefined> => {
    generationRef.current += 1
    const generation = generationRef.current
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    dispatch({ type: 'validate' })

    try {
      const result = await runWorkerJob<Input, Result>({
        createWorker: optionsRef.current.createWorker,
        validate: optionsRef.current.validate,
        validateResult: optionsRef.current.validateResult,
        transfer: optionsRef.current.transfer,
        timeoutMs: optionsRef.current.timeoutMs,
        timeoutCode: optionsRef.current.timeoutCode,
        input,
        signal: controller.signal,
        onProgress: (progress) => {
          if (generationRef.current === generation) dispatch({ type: 'progress', progress })
        },
      })
      if (generationRef.current !== generation) return undefined
      dispatch({ type: 'success', result })
      invokeSafely(() => optionsRef.current.onSuccess?.(result))
      return result
    } catch (error) {
      if (generationRef.current !== generation) return undefined
      if (isAbortError(error)) {
        dispatch({ type: 'cancel' })
        invokeSafely(() => optionsRef.current.onCancel?.())
        return undefined
      }
      const workerError = error instanceof WorkerJobError
        ? error
        : new WorkerJobError('fatal', 'worker_failed')
      dispatch({ type: 'error', error: { kind: workerError.kind, code: workerError.code } })
      invokeSafely(() => optionsRef.current.onError?.({ kind: workerError.kind, code: workerError.code }))
      return undefined
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null
    }
  }, [])

  useEffect(() => () => {
    generationRef.current += 1
    controllerRef.current?.abort()
    controllerRef.current = null
  }, [])

  return { state, run, cancel, reset }
}

function invokeSafely(callback: () => void): void {
  try {
    callback()
  } catch {
    // App callbacks run after terminal state is fixed and cannot change the worker outcome.
  }
}
